import { useCallback, useEffect, useState } from 'react'

import type { GatewayConfig } from '@/core/api'
import type { GatewayUserSettings } from '@/core/utils/interfaces/gatewayUserSettings.interface'

import type { GatewaySettingsFormErrors } from '../interfaces/gatewaySettingsFormErrors.interface'
import type { GatewaySettingsFormField } from '../interfaces/gatewaySettingsFormField.interface'
import { hasGatewaySettingsFormErrors } from '../utils/hasGatewaySettingsFormErrors'
import { validateGatewaySettingsForm } from '../utils/validateGatewaySettingsForm'

interface UseGatewaySettingsDialogParams {
  open: boolean
  gatewaySettings: GatewayUserSettings
  baseConfig: GatewayConfig
  applyGatewaySettings: (settings: GatewayUserSettings) => void
  resetGatewaySettings: () => void
  onRefetchGateway: () => Promise<unknown>
  onOpenChange: (open: boolean) => void
}

const buildFormValues = (gatewaySettings: GatewayUserSettings, baseConfig: GatewayConfig): GatewayUserSettings => {
  if (gatewaySettings.useCustom) {
    return gatewaySettings
  }

  return {
    useCustom: true,
    host: baseConfig.host,
    httpPort: baseConfig.port ?? '',
    wsPort: baseConfig.port ?? '',
    mcpPort: baseConfig.port ?? '',
  }
}

export function useGatewaySettingsDialog({
  open,
  gatewaySettings,
  baseConfig,
  applyGatewaySettings,
  resetGatewaySettings,
  onRefetchGateway,
  onOpenChange,
}: UseGatewaySettingsDialogParams) {
  const [formValues, setFormValues] = useState<GatewayUserSettings>(() => buildFormValues(gatewaySettings, baseConfig))
  const [errors, setErrors] = useState<GatewaySettingsFormErrors>({})

  useEffect(() => {
    if (open) {
      setFormValues(buildFormValues(gatewaySettings, baseConfig))
      setErrors({})
    }
  }, [open, gatewaySettings, baseConfig])

  const updateField = useCallback((field: GatewaySettingsFormField, value: string) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }))
  }, [])

  const handleSave = useCallback(() => {
    const nextSettings: GatewayUserSettings = {
      useCustom: true,
      host: formValues.host?.trim(),
      httpPort: formValues.httpPort?.trim(),
      wsPort: formValues.wsPort?.trim() || undefined,
      mcpPort: formValues.mcpPort?.trim() || undefined,
    }

    const nextErrors = validateGatewaySettingsForm(nextSettings)
    setErrors(nextErrors)

    if (hasGatewaySettingsFormErrors(nextErrors)) {
      return
    }

    applyGatewaySettings(nextSettings)
    onOpenChange(false)
    void onRefetchGateway()
  }, [
    applyGatewaySettings,
    formValues.host,
    formValues.httpPort,
    formValues.mcpPort,
    formValues.wsPort,
    onOpenChange,
    onRefetchGateway,
  ])

  const handleReset = useCallback(() => {
    resetGatewaySettings()
    onOpenChange(false)
    void onRefetchGateway()
  }, [onOpenChange, onRefetchGateway, resetGatewaySettings])

  const handleDismiss = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  return {
    formValues,
    errors,
    updateField,
    handleSave,
    handleReset,
    handleDismiss,
  }
}
